// Libraries
import { ChakraProps } from '@chakra-ui/react';
import { FunctionComponent, ReactElement } from 'react';

/**
 * Higher-order component that adds custom scrollbar styling to a component.
 *
 * @param {FunctionComponent<ChakraProps>} Component - The component to wrap with the custom scrollbar styling.
 * @returns {FunctionComponent<ChakraProps>} The wrapped component with custom scrollbar styling.
 */
export const withScroll =
  (Component: FunctionComponent<ChakraProps>): FunctionComponent<ChakraProps> =>
  /**
   * Wrapper component that applies custom scrollbar styling to the wrapped component.
   *
   * @param {ChakraProps} props - The props to be passed to the wrapped component.
   * @returns {ReactElement} The rendered wrapped component with custom scrollbar styling.
   */
  (props: ChakraProps): ReactElement => {
    /**
     * Render the wrapped component with custom scrollbar styling.
     *
     * @returns {ReactElement} The rendered wrapped component with custom scrollbar styling.
     */
    return (
      <Component
        sx={{
          '&::-webkit-scrollbar': {
            width: '5px',
            backgroundColor: 'scroll.50',
          },
          '&::-webkit-track': {
            backgroundColor: 'scroll.50',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'scroll.90',
          },
        }}
        {...props}
      />
    );
  };
